"use client";

import { AuthUIProvider } from "@daveyplate/better-auth-ui";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { authClient } from "@/lib/auth-client";

/** Reuse the app's existing image pipeline (S3 + CloudFront) for user avatars. */
async function uploadAvatar(file: File): Promise<string | null> {
  const data = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

  const res = await fetch("/api/images/upload", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ data }),
  });
  if (!res.ok) return null;
  const { id } = (await res.json()) as { id: string };
  return `https://${process.env.NEXT_PUBLIC_CLOUDFRONT_ID}.cloudfront.net/${id}`;
}

/**
 * Better Auth UI provider. Replaces ClerkProvider. Hookless client store under
 * the hood, so it just needs the auth client + Next.js navigation adapters.
 */
export function Providers({ children }: { children: React.ReactNode }) {
  const router = useRouter();

  return (
    <AuthUIProvider
      authClient={authClient}
      navigate={router.push}
      replace={router.replace}
      onSessionChange={() => router.refresh()}
      // Auth views live at root paths (/sign-in, /sign-up, /forgot-password, ...)
      // to keep the pre-migration URLs; see app/(public)/(signinup)/[authView].
      basePath="/"
      redirectTo="/orgs"
      organization={{
        basePath: "/orgs",
        pathMode: "slug",
        personalPath: "/orgs",
      }}
      avatar={{ upload: uploadAvatar }}
      nameRequired
      Link={Link}
    >
      {children}
    </AuthUIProvider>
  );
}
